<template>
  <v-dialog
      v-model="dialog"
      persistent
      scrollable
      :fullscreen="$vuetify.display.xs"
      :width="$vuetify.display.smAndUp ? 600 : 'auto'"
      style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" >
        <slot></slot>
      </div>
    </template>

    <v-form
      ref="form"
      v-on:submit.prevent="createUser"
      v-bind:disabled="!currentNode"
      autocomplete="off"
      data-cy="dialog-createUser-form">

      <v-card v-cloak>
        <v-card-title class="d-flex justify-space-between"
                      data-cy="dialog-createUser-title">
          {{ $t('routes.users.createUser') }}

          <v-btn icon variant="text" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pb-0 pt-4 scrollable">

          <div v-if="!currentNode" class="bg-error mb-2">
            {{ $t('menus.createNode.noActiveNode') }}
          </div>

          <div v-else class="mb-2">
            <div style="line-height: 1.2;">
              {{ $t('menus.createUser.usersNewRootNode') }}
            </div>
            <div>
              <SelectNodeDialog
                v-model="selectedNodeId"
                v-slot:default="slotProps"
                :user-type="selectedUserType === userTypeIdsEnum.TEAM_MEMBER ? userTypesEnum.TEAM_MEMBER : userTypesEnum.CLIENT"
              >
                <v-btn elevation=0 block color="info" data-cy="dialog-createUser-selectNode-button">
                  <span v-if="slotProps && slotProps.selectedNode">
                    {{ slotProps.selectedNode.name }}
                  </span>
                </v-btn>
              </SelectNodeDialog>
            </div>
          </div>

          <v-text-field
            v-model="firstName"
            :placeholder="$t('menus.createUser.firstName')"
            :rules="firstNameRules"
            autocomplete="off"
            required block
            variant="solo-filled" flat
            data-cy="dialog-createUser-firstName"
          />

          <v-text-field
            v-model="lastName"
            :placeholder="$t('menus.createUser.lastName')"
            :rules="lastNameRules"
            autocomplete="off"
            required block
            variant="solo-filled" flat
            data-cy="dialog-createUser-lastName"
          />

          <v-text-field
            v-model="email"
            :placeholder="$t('menus.createUser.email')"
            :rules="emailRules"
            :error-messages="emailErrors"
            autocomplete="off"
            required block
            variant="solo-filled" flat
            data-cy="dialog-createUser-email"
          />

          <div data-cy="dialog-createUser-userType" v-if="isTeamMember">
            <v-select
              v-model="selectedUserType"
              :items="getUserTypes"
              :rules="selectedUserTypeRules"
              item-title="name"
              item-value="id"
              :placeholder="$t('menus.createUser.userType')"
              required block
              variant="solo-filled" flat
            />
          </div>

          <div class="d-flex">
            <v-text-field
              type="password"
              v-model="password"
              :placeholder="$t('menus.createUser.password')"
              :rules="passwordRules"
              autocomplete="new-password"
              variant="solo-filled"
              data-cy="dialog-createUser-password"
              id="create-user-form-password-id"
              required block flat
            />
            <v-tooltip location="bottom">
              <template v-slot:activator="{props}">
                <v-btn
                  v-bind="props"
                  v-on:click="generatePassword"
                  color="default"
                  rounded="10"
                  variant="flat"
                  class="ml-1"
                  data-cy="accountCreateForm-generate-password-btn"
                >
                  <v-icon>mdi-lock-reset</v-icon>
                </v-btn>
              </template>

              <span>{{ $t('routes.users.generatePassword') }}</span>
            </v-tooltip>
          </div>

          <div>
            <v-text-field
              type="password"
              :placeholder="$t('menus.createUser.repassword')"
              v-model="repassword"
              :rules="[passwordConfirmationRule]"
              autocomplete="new-password"
              required block
              variant="solo-filled" flat
              data-cy="dialog-createUser-rePassword"
            />
          </div>

          <div class="bg-error" v-if="errorMessage">
            {{ errorMessage }}
          </div>

          <div class="bg-error" v-if="errorTranslatable">
            {{
              $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content)
            }}
          </div>

          <div class="bg-error" v-if="userLimitExceeded">
            {{ $t('menus.createUser.usersLimitReached') }}
          </div>

          <div class="bg-error" v-if="clientuserLimitExceeded">
            {{ $t('menus.createUser.clientusersLimitReached') }}
          </div>

          <!-- TODO: This part is not implemented yet -->
          <!-- <div
            class="error"
            v-if="$canControlTenantConfig() && (userLimitExceeded || clientuserLimitExceeded)"
            v-on:click="showMenu('ResourceLimitsMenu')"
            style="cursor: pointer;"
          >
            {{$t('menus.createUser.purchaseMoreUsers')}}
          </div> -->


        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            type="submit"
            class="bg-primary"
            :disabled="isSubmitDisabled || isFormSubmitted"
            elevation=0
            data-cy="dialog-createUser-submit-btn"
          >
            {{ $t('routes.users.createUser') }}
          </v-btn>
          <v-btn
            variant="text"
            :disabled="isFormSubmitted"
            @click="dialog = false"
            data-cy="dialog-createUser-false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import { useServiceStore } from '@/stores/ServiceStore.js'
import _toLower from 'lodash/toLower'
import SelectNodeDialog from "./SelectNodeDialog.vue"
import Helper from '../../../../../components/helper.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: "CreateUserDialog",
  components: {
    SelectNodeDialog,
  },
  data: function() {
    return {
      dialog: false,
      errorMessage: "",
      errorTranslatable: null,
      selectedNodeId: null,

      firstName: "",
      firstNameRules: Helper.getNameValidationRules(this, {
        required: true,
        messages: {
          required: 'menus.createUser.firstnameRequired',
        },
      }),

      lastName: "",
      lastNameRules: Helper.getNameValidationRules(this, {
        required: true,
        messages: {
          required: 'menus.createUser.lastnameRequired',
        },
      }),

      email: "",
      emailErrors: [],
      emailRules: Helper.getEmailValidationRules(this),

      selectedUserType: null,
      selectedUserTypeRules: [
        v => !!v || this.$t('menus.createUser.userTypeRequired'),
      ],

      password: "",
      passwordRules: Helper.getPasswordRules(this),

      repassword: "",

      isFormSubmitted: false,
    };
  },
  watch: {
    password: function(val) {
      const newVal = val ? val.trim() : '';
      if (!newVal || newVal === '') {
        this.repassword = '';
      }
    },
    dialog(newValue) {
      this.resetForm();

      if (!newValue) {
        this.selectedNodeId = null;
      } else {
        // Dialog is opening
        const authStore = useAuthStore();
        const activeAccessPointId = authStore.rootNodeId;
        this.selectedNodeId = activeAccessPointId;

        const payload = {
          accessPointId: activeAccessPointId,
        };

        const serviceStore = useServiceStore();
        serviceStore.refreshLimits(payload);

        // Check ability and close immediately if needed
        if (!this.$canControlUser()) {
          this.$nextTick(() => { // Ensure DOM update before closing
            this.dialog = false;
          });
        }
      }
    },
    currentNode() {
      this.updateSelectedNodeId();
    },
    activeRootNodeId(rootNodeId) {
      if (this.dialog && rootNodeId) {
        this.selectedNodeId = rootNodeId;
      }
    },
    selectedNodeId() {
      if (this.isCreatingClientUserInTeamOnlyFolder) {
        this.errorMessage = this.$t('menus.createUser.cannotCreateClientInsideTeamOnlyFolder');
      } else {
        this.errorMessage = '';
      }
    },
    selectedUserType() {
      if (this.isCreatingClientUserInTeamOnlyFolder) {
        this.errorMessage = this.$t('menus.createUser.cannotCreateClientInsideTeamOnlyFolder');
      } else {
        this.errorMessage = '';
      }
    },
  },
  methods: {
    resetForm() {
      // Reset validation state and form fields
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      // Reset all form state variables
      this.errorMessage = '';
      this.errorTranslatable = null;
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.emailErrors = [];
      this.selectedUserType = null;
      this.password = '';
      this.repassword = '';
      this.isFormSubmitted = false;

      // Set the selected node to current node if available
      this.updateSelectedNodeId();
    },
    updateSelectedNodeId: function() {
      // Set the selected node - the users new home directory
      if (
        this.currentNode &&
        this.currentNode.id &&
        this.selectedNodeId === null
      ) {
        this.selectedNodeId = this.currentNode.id;
      }
    },
    generatePassword: function() {
      const passwordLength = 10;
      const randPassword = Helper.generatePassword(passwordLength);

      this.password = randPassword;
      this.repassword = randPassword;
    },
    async createUser() { // Convert to async
      if (this.isFormSubmitted) {
        return;
      }

      this.errorMessage = "";
      this.errorTranslatable = null;

      // Await validation result
      const { valid: validForm } = await this.$refs.form.validate();
      if (!validForm) {
        return;
      }

      this.email = _toLower(this.email);

      let formData = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        password: this.password,
        nodeid: this.selectedNodeId,
        userType: this.isTeamMember ? this.selectedUserType : this.userTypeIdsEnum.CLIENT,
      };

      this.isFormSubmitted = true;

      const userStore = useUserStore();

      try {
        // Await the API call
        const user = await userStore.createUser(formData);

        this.dialog = false;

        const payload = {
          user,
          accessPointId: this.selectedNodeId, // Use 'this' directly
        };

        this.$emit("userCreated", payload); // Use 'this' directly

      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const defaultMessage = this.$t('backend.system-error'); // Use 'this' directly
          const apiError = error.response.data.error;
          this.errorMessage = this.$localizeMessage(apiError.message, defaultMessage); // Use 'this' directly
        } else {
          this.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      } finally {
        // This runs after try or catch block finishes
        this.isFormSubmitted = false;
      }
    },
    handleKeyDown(event) {
      if (!this.isFormSubmitted && (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27)) {
        this.dialog = false;
      }
    }
  },
  computed: {
    activeRootNodeId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    passwordConfirmationRule() {
      return this.password === this.repassword || this.$t('menus.createUser.passwordsNotMatch');
    },
    currentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    getUserTypes: function() {
      // the DB values of the user types
      const userStore = useUserStore();
      return userStore.getUsertypes;
    },
    isSubmitDisabled: function() {
      return this.userLimitExceeded || this.clientuserLimitExceeded || this.isCreatingClientUserInTeamOnlyFolder;
    },
    userTypeIdsEnum: function() {
      // the ENUM of the user types
      const commonStore = useCommonStore();
      return commonStore.userTypes;
    },
    userTypesEnum: function () {
      // Slightly different version of "userTypeIdsEnum" which returns a human-readable
      // user type instead of its UUID as stored in the database.
      const commonStore = useCommonStore();
      return commonStore.userType;
    },
    getLimits: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getLimits;
    },
    userLimitExceeded: function() {
      if (!Array.isArray(this.getLimits)) return false;
      if (this.selectedUserType !== this.userTypeIdsEnum.TEAM_MEMBER) return false;

      const userLimit = this.getLimits.find(x => x.name === 'users');

      return userLimit &&
          typeof userLimit.spent === "number" &&
          typeof userLimit.limit === "number" &&
          userLimit.spent >= userLimit.limit;
    },
    clientuserLimitExceeded: function() {
      if (!Array.isArray(this.getLimits)) return false;
      if (this.selectedUserType !== this.userTypeIdsEnum.CLIENT) return false;

      const userLimit = this.getLimits.find(x => x.name === 'clientuser');

      return userLimit &&
          typeof userLimit.spent === "number" &&
          typeof userLimit.limit === "number" &&
          userLimit.spent >= userLimit.limit;
    },
    isCreatingClientUserInTeamOnlyFolder() {
      if (this.selectedNodeId && this.selectedUserType) {
        const nodeStore = useNodeStore();
        const node = nodeStore.getNodeById(this.selectedNodeId);

        return node.teamonly === 1 && this.selectedUserType === this.userTypeIdsEnum.CLIENT;
      }

      return false;
    },
    isTeamMember() {
      return this.$isTeamUser()
    }
  },
}
</script>
